import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, ZoomIn, ZoomOut, Maximize2, Minimize2, FileText, ExternalLink } from "lucide-react";
import { API } from "@/library/lib/api";

const PdfViewerModal = ({ fileId, onClose }) => {
  const [zoom, setZoom] = useState(100);
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!fileId) return;
    setLoading(true);
    setError(false);
    setZoom(100);
    fetch(`${API}/files/${fileId}`).then((r) => r.json()).then(setMeta).catch(() => {});
  }, [fileId]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(300, z + 10));
      if (e.key === "-") setZoom((z) => Math.max(40, z - 10));
    };
    if (fileId) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [fileId, onClose]);

  if (!fileId) return null;
  const fileUrl = `${API}/files/${fileId}/download`;
  const downloadName = meta?.file?.original_filename || "document.pdf";

  return (
    <AnimatePresence>
      <motion.div
        data-testid="pdf-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, y: 8, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className={`bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden ${
            fullscreen ? "w-full h-full" : "w-full max-w-6xl h-[92vh]"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 sm:px-5 py-3 border-b border-slate-200 bg-slate-50 gap-2">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-brand" />
              </div>
              <div className="min-w-0">
                <div className="font-heading font-bold text-brand-dark truncate text-sm sm:text-base">
                  {meta?.file?.title || "عرض الملف"}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {meta?.indicator?.name || ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <button data-testid="pdf-zoom-out" onClick={() => setZoom(Math.max(40, zoom - 10))} className="p-2 hover:bg-slate-200 rounded-lg" title="تصغير الصفحة"><ZoomOut className="w-4 h-4" /></button>
              <span data-testid="pdf-zoom-value" className="text-xs font-bold text-slate-600 w-12 text-center tabular-nums">{zoom}%</span>
              <button data-testid="pdf-zoom-in" onClick={() => setZoom(Math.min(300, zoom + 10))} className="p-2 hover:bg-slate-200 rounded-lg" title="تكبير الصفحة"><ZoomIn className="w-4 h-4" /></button>
              <button data-testid="pdf-reset-zoom" onClick={() => setZoom(100)} className="hidden sm:block px-2 py-1 hover:bg-slate-200 rounded-lg text-[11px] font-bold text-slate-600" title="إعادة الضبط">100%</button>
              <div className="w-px h-6 bg-slate-300 mx-1" />
              <button data-testid="pdf-fullscreen" onClick={() => setFullscreen(!fullscreen)} className="p-2 hover:bg-slate-200 rounded-lg" title={fullscreen ? "خروج من ملء الشاشة" : "ملء الشاشة"}>
                {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <a data-testid="pdf-open-tab" href={fileUrl} target="_blank" rel="noreferrer" className="p-2 hover:bg-slate-200 rounded-lg text-slate-600" title="فتح في نافذة جديدة"><ExternalLink className="w-4 h-4" /></a>
              <a data-testid="pdf-download-btn" href={fileUrl} download={downloadName} className="flex items-center gap-1.5 px-3 py-2 bg-brand text-white rounded-lg text-sm font-semibold hover:bg-brand-light transition-colors">
                <Download className="w-4 h-4" /> <span className="hidden sm:inline">تحميل</span>
              </a>
              <button data-testid="pdf-close-btn" onClick={onClose} className="p-2 hover:bg-red-100 text-slate-600 hover:text-red-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
          </div>
          {/* Viewer */}
          <div className="flex-1 bg-slate-200 overflow-auto relative" data-testid="pdf-viewer-area">
            {loading && !error && (
              <div className="absolute inset-0 flex items-center justify-center text-slate-500 pointer-events-none z-10">
                <div className="text-center bg-white/80 backdrop-blur rounded-xl p-6">
                  <div className="w-12 h-12 border-4 border-brand/30 border-t-brand rounded-full animate-spin mx-auto mb-3"></div>
                  جاري تحميل الملف...
                </div>
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center text-slate-600">
                <div className="text-center bg-white rounded-xl p-8 shadow">
                  <FileText className="w-12 h-12 text-red-400 mx-auto mb-3" />
                  <div className="font-bold mb-2">تعذّر عرض الملف داخل المتصفح</div>
                  <a href={fileUrl} target="_blank" rel="noreferrer" className="text-brand font-bold underline">افتح الملف في نافذة جديدة</a>
                </div>
              </div>
            )}
            <div
              className="origin-top mx-auto transition-transform duration-150"
              style={{
                width: `${10000 / zoom}%`,
                height: `${10000 / zoom}%`,
                transform: `scale(${zoom / 100})`,
              }}
            >
              <iframe
                data-testid="pdf-iframe"
                title="PDF Viewer"
                src={fileUrl + "#view=FitH"}
                className="w-full h-full border-0 bg-white"
                onLoad={() => setLoading(false)}
                onError={() => { setLoading(false); setError(true); }}
              />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PdfViewerModal;
